<template>
  <div class="ev-config-panel" v-loading="loading">
    <div class="config-toolbar">
      <span class="hint">修改参数后请点击「应用配置」写入 Redis + Mongo；首次无配置时展示前端默认种子值</span>
      <div class="toolbar-actions">
        <el-tag v-if="saving" size="mini" type="warning">保存中...</el-tag>
        <el-tag v-else-if="dirty" size="mini" type="info">待保存</el-tag>
        <el-tag v-else-if="usingSeed" size="mini" type="warning">Map 未入库（请应用）</el-tag>
        <el-tag v-else size="mini" type="success">已同步</el-tag>
        <el-button size="mini" type="primary" :loading="saving" :disabled="!dirty" @click="saveConfig">应用配置</el-button>
        <el-button size="mini" :loading="resetting" @click="onReset">恢复默认</el-button>
      </div>
    </div>

    <div class="config-scroll">
      <el-collapse v-model="activeSections">
        <el-collapse-item title="W_loc — 城市权重" name="wLoc">
          <el-table :data="areaIds.map(id => ({ areaId: id }))" size="mini" border class="compact-table">
            <el-table-column label="区域" width="160">
              <template slot-scope="scope">{{ areaLabel(scope.row.areaId) }}</template>
            </el-table-column>
            <el-table-column label="权重" width="140">
              <template slot-scope="scope">
                <el-input-number :value="wLocValue(scope.row.areaId)" :step="0.1" :precision="2" :controls="false"
                  size="mini" @change="v => updateWLoc(scope.row.areaId, v)"></el-input-number>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="r_pts — 区域积分率" name="rPts">
          <el-table :data="areaIds.map(id => ({ areaId: id }))" size="mini" border class="compact-table">
            <el-table-column label="区域" width="160">
              <template slot-scope="scope">{{ areaLabel(scope.row.areaId) }}</template>
            </el-table-column>
            <el-table-column label="积分率" width="140">
              <template slot-scope="scope">
                <el-input-number :value="rPtsValue(scope.row.areaId)" :step="1" :precision="1" :controls="false"
                  size="mini" @change="v => updateRPts(scope.row.areaId, v)"></el-input-number>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="W_time — 全局时段权重" name="wTimeGlobal">
          <div class="section-actions">
            <el-button size="mini" icon="el-icon-plus" @click="addWTimeHour">添加小时</el-button>
          </div>
          <el-table :data="wTimeHourRowsLocal" size="mini" border class="compact-table" row-key="uid">
            <el-table-column label="小时" width="120">
              <template slot-scope="scope">
                <el-input :value="scope.row.hour" size="mini" type="number" min="0" max="23"
                  @input="v => setWTimeHourField(scope.$index, 'hour', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="权重" width="140">
              <template slot-scope="scope">
                <el-input :value="scope.row.weight" size="mini" type="number" step="0.1"
                  @input="v => setWTimeHourField(scope.$index, 'weight', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="removeWTimeHour(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="W_time — 分区域时段权重" name="wTimeArea">
          <div class="section-actions">
            <el-button size="mini" icon="el-icon-plus" @click="addWTimeAreaHour">添加</el-button>
          </div>
          <el-table :data="wTimeAreaHourRowsLocal" size="mini" border class="compact-table" row-key="uid">
            <el-table-column label="区域" width="160">
              <template slot-scope="scope">
                <el-select :value="scope.row.areaId" size="mini" placeholder="区域"
                  @change="v => setWTimeAreaField(scope.$index, 'areaId', v)">
                  <el-option v-for="item in areaOptions" :key="item.id" :label="areaLabel(item.id)"
                    :value="item.id"></el-option>
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="小时" width="100">
              <template slot-scope="scope">
                <el-input :value="scope.row.hour" size="mini" type="number" min="0" max="23"
                  @input="v => setWTimeAreaField(scope.$index, 'hour', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="权重" width="140">
              <template slot-scope="scope">
                <el-input :value="scope.row.weight" size="mini" type="number" step="0.1"
                  @input="v => setWTimeAreaField(scope.$index, 'weight', v)"></el-input>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80">
              <template slot-scope="scope">
                <el-button type="text" size="mini" @click="removeWTimeAreaHour(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-collapse-item>

        <el-collapse-item title="θ — ROI 断点 (roiBreakpoints)" name="roi">
          <breakpoint-editor v-model="config.roiBreakpoints" @change="markDirty" />
        </el-collapse-item>

        <el-collapse-item title="γ — Gamma 断点 (空驶发生概率)" name="gamma">
          <breakpoint-editor v-model="config.gammaBreakpoints" @change="markDirty" />
        </el-collapse-item>

        <el-collapse-item title="β — Beta 断点 (拼车成单衰减系数)" name="beta">
          <breakpoint-editor v-model="config.betaBreakpoints" @change="markDirty" />
        </el-collapse-item>

        <el-collapse-item title="μ — 均衡系数" name="mu">
          <el-form label-width="160px" size="mini" class="mu-form">
            <el-form-item label="预判窗口 (小时)">
              <el-input-number v-model="config.muHorizonHours" :min="1" :max="72" :step="1" :controls="false"
                @change="markDirty"></el-input-number>
            </el-form-item>
            <el-form-item label="安全缓冲 K">
              <el-input-number v-model="config.muSafeBufferK" :min="0" :max="20" :step="1" :controls="false"
                @change="markDirty"></el-input-number>
            </el-form-item>
            <el-form-item label="短缺溢价">
              <el-input-number v-model="config.muShortagePremium" :min="0" :step="0.1" :precision="2" :controls="false"
                @change="markDirty"></el-input-number>
            </el-form-item>
            <el-form-item label="均衡价值">
              <el-input-number v-model="config.muBalancedValue" :min="0" :step="0.1" :precision="2" :controls="false"
                @change="markDirty"></el-input-number>
            </el-form-item>
            <el-form-item label="λ (muLambda)">
              <el-input-number v-model="config.muLambda" :min="0" :max="1" :step="0.05" :precision="2" :controls="false"
                @change="markDirty"></el-input-number>
            </el-form-item>
            <el-form-item label="饱和阈值 (sat)">
              <el-input-number v-model="config.muSaturationThreshold" :min="2" :max="50" :step="1" :controls="false"
                @change="markDirty"></el-input-number>
              <span class="form-hint">X ≥ sat 时 μ 固定为饱和值；K+1 ~ sat−1 每整数一档</span>
            </el-form-item>
            <el-form-item label="饱和 μ 值">
              <el-input-number v-model="config.muSaturatedValue" :min="0" :max="1" :step="0.01" :precision="3"
                :controls="false" @change="markDirty"></el-input-number>
            </el-form-item>
            <el-form-item label="调位成本倍率">
              <el-input-number v-model="config.repositionMultiplier" :min="0" :max="20" :step="0.5" :precision="1"
                :controls="false" @change="markDirty"></el-input-number>
              <span class="form-hint">emptyTrip 惩罚 = valuePoints × V_base × W_loc × 倍率</span>
            </el-form-item>
          </el-form>
        </el-collapse-item>
      </el-collapse>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { getEvTunableConfig, applyEvTunableConfig } from "@/api/ev_solver";
import { createSeedTunableConfig, mergeTunableWithSeed, hasUnpersistedMaps } from "../../utils/evTunableDefaults";
import BreakpointEditor from "./BreakpointEditor";

const DEFAULT_AREA_IDS = [1, 2, 3, 4];
let rowUid = 0;

function nextRowUid() {
  rowUid += 1;
  return `wtime_${rowUid}`;
}

function emptyConfig() {
  const seed = createSeedTunableConfig();
  return {
    wLoc: {},
    rPtsByArea: {},
    wTimeByHour: {},
    wTimeByAreaHour: {},
    roiBreakpoints: [],
    gammaBreakpoints: [],
    betaBreakpoints: [],
    muHorizonHours: seed.muHorizonHours,
    muSafeBufferK: seed.muSafeBufferK,
    muShortagePremium: seed.muShortagePremium,
    muBalancedValue: seed.muBalancedValue,
    muLambda: seed.muLambda,
    muSaturationThreshold: seed.muSaturationThreshold,
    muSaturatedValue: seed.muSaturatedValue,
    repositionMultiplier: seed.repositionMultiplier
  };
}

export default {
  components: { BreakpointEditor },

  data() {
    return {
      loading: false,
      saving: false,
      resetting: false,
      dirty: false,
      usingSeed: false,
      loadSeq: 0,
      wTimeInitialized: false,
      activeSections: ["wLoc", "rPts", "wTimeGlobal", "wTimeArea", "roi", "gamma", "beta", "mu"],
      config: emptyConfig(),
      wTimeHourRowsLocal: [],
      wTimeAreaHourRowsLocal: []
    };
  },

  computed: {
    ...mapState({
      areaOpts: (state) => state.areaOpts
    }),

    areaOptions() {
      if (this.areaOpts && this.areaOpts.length) {
        return this.areaOpts;
      }
      return DEFAULT_AREA_IDS.map((id) => ({ id, name: `区域${id}` }));
    },

    areaIds() {
      const fromConfig = [
        ...Object.keys(this.config.wLoc || {}),
        ...Object.keys(this.config.rPtsByArea || {}),
        ...Object.keys(this.config.wTimeByAreaHour || {})
      ].map(Number);
      const ids = [...new Set([...DEFAULT_AREA_IDS, ...fromConfig])];
      return ids.sort((a, b) => a - b);
    }
  },

  mounted() {
    this.loadConfig();
  },

  methods: {
    areaLabel(areaId) {
      const area = this.areaOptions.find((item) => item.id === areaId);
      return area ? `${area.name}(${areaId})` : `区域${areaId}`;
    },

    loadConfig() {
      const reqId = ++this.loadSeq;
      this.loading = true;
      return getEvTunableConfig({ _hideLoading: true })
        .then((res) => {
          if (reqId !== this.loadSeq) return;
          const serverData = res.data;
          const needsPersist = hasUnpersistedMaps(serverData);
          this.config = this.normalizeConfig(mergeTunableWithSeed(serverData));
          this.syncWTimeRowsFromConfig();
          this.wTimeInitialized = true;
          this.usingSeed = needsPersist;
          this.dirty = needsPersist;
        })
        .finally(() => {
          if (reqId === this.loadSeq) {
            this.loading = false;
          }
        });
    },

    normalizeConfig(data) {
      const src = data || {};
      const seed = createSeedTunableConfig();
      return {
        wLoc: this.normalizeNumberKeyMap(src.wLoc),
        rPtsByArea: this.normalizeNumberKeyMap(src.rPtsByArea),
        wTimeByHour: this.normalizeNumberKeyMap(src.wTimeByHour),
        wTimeByAreaHour: this.normalizeNestedNumberKeyMap(src.wTimeByAreaHour),
        roiBreakpoints: (src.roiBreakpoints || []).map((bp) => ({ minutes: bp.minutes, value: bp.value })),
        gammaBreakpoints: (src.gammaBreakpoints || []).map((bp) => ({ minutes: bp.minutes, value: bp.value })),
        betaBreakpoints: (src.betaBreakpoints || []).map((bp) => ({ minutes: bp.minutes, value: bp.value })),
        muHorizonHours: src.muHorizonHours != null ? src.muHorizonHours : seed.muHorizonHours,
        muSafeBufferK: src.muSafeBufferK != null ? src.muSafeBufferK : seed.muSafeBufferK,
        muShortagePremium: src.muShortagePremium != null ? src.muShortagePremium : seed.muShortagePremium,
        muBalancedValue: src.muBalancedValue != null ? src.muBalancedValue : seed.muBalancedValue,
        muLambda: src.muLambda != null ? src.muLambda : seed.muLambda,
        muSaturationThreshold: src.muSaturationThreshold != null ? src.muSaturationThreshold : seed.muSaturationThreshold,
        muSaturatedValue: src.muSaturatedValue != null ? src.muSaturatedValue : seed.muSaturatedValue,
        repositionMultiplier: src.repositionMultiplier != null ? src.repositionMultiplier : seed.repositionMultiplier
      };
    },

    normalizeNumberKeyMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((key) => {
        const n = Number(key);
        if (!Number.isNaN(n) && map[key] != null) {
          result[n] = map[key];
        }
      });
      return result;
    },

    normalizeNestedNumberKeyMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((areaKey) => {
        const areaId = Number(areaKey);
        if (!Number.isNaN(areaId)) {
          result[areaId] = this.normalizeNumberKeyMap(map[areaKey]);
        }
      });
      return result;
    },

    syncWTimeRowsFromConfig() {
      const map = this.config.wTimeByHour || {};
      this.wTimeHourRowsLocal = Object.keys(map)
        .map(Number)
        .sort((a, b) => a - b)
        .map((hour) => ({
          uid: nextRowUid(),
          hour,
          weight: map[hour]
        }));

      const nested = this.config.wTimeByAreaHour || {};
      const rows = [];
      Object.keys(nested).forEach((areaKey) => {
        const areaId = Number(areaKey);
        const byHour = nested[areaKey] || {};
        Object.keys(byHour).forEach((hourKey) => {
          rows.push({
            uid: nextRowUid(),
            areaId,
            hour: Number(hourKey),
            weight: byHour[hourKey]
          });
        });
      });
      this.wTimeAreaHourRowsLocal = rows.sort((a, b) => a.areaId - b.areaId || a.hour - b.hour);
    },

    syncWTimeRowsToConfig() {
      const wTimeByHour = {};
      this.wTimeHourRowsLocal.forEach((row) => {
        const hour = this.parseHour(row.hour);
        const weight = this.parseWeight(row.weight);
        if (hour == null || weight == null) return;
        wTimeByHour[hour] = weight;
      });
      this.config.wTimeByHour = wTimeByHour;

      const wTimeByAreaHour = {};
      this.wTimeAreaHourRowsLocal.forEach((row) => {
        const areaId = this.parseIntVal(row.areaId);
        const hour = this.parseHour(row.hour);
        const weight = this.parseWeight(row.weight);
        if (areaId == null || hour == null || weight == null) return;
        if (!wTimeByAreaHour[areaId]) {
          wTimeByAreaHour[areaId] = {};
        }
        wTimeByAreaHour[areaId][hour] = weight;
      });
      this.config.wTimeByAreaHour = wTimeByAreaHour;
    },

    parseIntVal(val) {
      if (val === "" || val == null) return null;
      const n = parseInt(val, 10);
      return Number.isNaN(n) ? null : n;
    },

    parseHour(val) {
      const n = this.parseIntVal(val);
      if (n == null || n < 0 || n > 23) return null;
      return n;
    },

    parseWeight(val) {
      if (val === "" || val == null) return null;
      const n = parseFloat(val);
      return Number.isNaN(n) ? null : n;
    },

    setWTimeHourField(index, field, rawVal) {
      const row = this.wTimeHourRowsLocal[index];
      if (!row) return;
      if (field === "hour") {
        this.$set(row, "hour", rawVal === "" ? "" : this.parseIntVal(rawVal));
      } else {
        this.$set(row, "weight", rawVal === "" ? "" : rawVal);
      }
      this.markDirty();
    },

    setWTimeAreaField(index, field, rawVal) {
      const row = this.wTimeAreaHourRowsLocal[index];
      if (!row) return;
      if (field === "areaId") {
        this.$set(row, "areaId", rawVal);
      } else if (field === "hour") {
        this.$set(row, "hour", rawVal === "" ? "" : this.parseIntVal(rawVal));
      } else {
        this.$set(row, "weight", rawVal === "" ? "" : rawVal);
      }
      this.markDirty();
    },

    copyNestedMap(source) {
      const result = {};
      if (!source) return result;
      Object.keys(source).forEach((areaKey) => {
        result[areaKey] = { ...(source[areaKey] || {}) };
      });
      return result;
    },

    buildPayload() {
      this.materializeAreaMaps();
      this.syncWTimeRowsToConfig();
      return {
        wLoc: this.toStringKeyMap(this.config.wLoc),
        rPtsByArea: this.toStringKeyMap(this.config.rPtsByArea),
        wTimeByHour: this.toStringKeyMap(this.config.wTimeByHour),
        wTimeByAreaHour: this.toNestedStringKeyMap(this.config.wTimeByAreaHour),
        roiBreakpoints: this.config.roiBreakpoints,
        gammaBreakpoints: this.config.gammaBreakpoints,
        betaBreakpoints: this.config.betaBreakpoints,
        muHorizonHours: this.config.muHorizonHours,
        muSafeBufferK: this.config.muSafeBufferK,
        muShortagePremium: this.config.muShortagePremium,
        muBalancedValue: this.config.muBalancedValue,
        muLambda: this.config.muLambda,
        muSaturationThreshold: this.config.muSaturationThreshold,
        muSaturatedValue: this.config.muSaturatedValue,
        repositionMultiplier: this.config.repositionMultiplier
      };
    },

    /** 保存前把表格展示值写入 config，避免仅 UI fallback 未落库 */
    materializeAreaMaps() {
      this.areaIds.forEach((areaId) => {
        if (this.config.wLoc[areaId] == null) {
          this.$set(this.config.wLoc, areaId, this.wLocValue(areaId));
        }
        if (this.config.rPtsByArea[areaId] == null) {
          this.$set(this.config.rPtsByArea, areaId, this.rPtsValue(areaId));
        }
      });
    },

    toStringKeyMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((key) => {
        const val = map[key];
        if (val != null) {
          result[String(key)] = val;
        }
      });
      return result;
    },

    toNestedStringKeyMap(map) {
      const result = {};
      Object.keys(map || {}).forEach((areaKey) => {
        result[String(areaKey)] = this.toStringKeyMap(map[areaKey]);
      });
      return result;
    },

    wLocValue(areaId) {
      return this.config.wLoc[areaId] != null ? this.config.wLoc[areaId] : 1;
    },

    rPtsValue(areaId) {
      return this.config.rPtsByArea[areaId] != null ? this.config.rPtsByArea[areaId] : 30;
    },

    updateWLoc(areaId, weight) {
      if (weight == null) return;
      this.$set(this.config.wLoc, areaId, weight);
      this.markDirty();
    },

    updateRPts(areaId, value) {
      if (value == null) return;
      this.$set(this.config.rPtsByArea, areaId, value);
      this.markDirty();
    },

    markDirty() {
      this.dirty = true;
    },

    saveConfig() {
      this.saving = true;
      const wTimeHourSnapshot = this.wTimeHourRowsLocal.map((row) => ({ ...row }));
      const wTimeAreaSnapshot = this.wTimeAreaHourRowsLocal.map((row) => ({ ...row }));
      const payload = this.buildPayload();
      return applyEvTunableConfig(payload)
        .then((res) => {
          this.mergeConfigAfterApply(res.data, wTimeHourSnapshot, wTimeAreaSnapshot);
          this.usingSeed = false;
          this.dirty = false;
          this.$message.success("配置已应用并保存");
        })
        .finally(() => {
          this.saving = false;
        });
    },

    mergeConfigAfterApply(serverData, wTimeHourRows, wTimeAreaRows) {
      this.config = this.normalizeConfig(serverData);
      this.wTimeHourRowsLocal = wTimeHourRows;
      this.wTimeAreaHourRowsLocal = wTimeAreaRows;
      this.syncWTimeRowsToConfig();
    },

    addWTimeHour() {
      const used = this.wTimeHourRowsLocal.map((r) => this.parseHour(r.hour)).filter((h) => h != null);
      let hour = 17;
      while (used.includes(hour) && hour < 24) hour += 1;
      if (hour >= 24) hour = 0;
      while (used.includes(hour) && hour < 24) hour += 1;
      this.wTimeHourRowsLocal = [
        ...this.wTimeHourRowsLocal,
        { uid: nextRowUid(), hour, weight: 1 }
      ];
      this.markDirty();
    },

    removeWTimeHour(index) {
      this.wTimeHourRowsLocal = this.wTimeHourRowsLocal.filter((_, i) => i !== index);
      this.markDirty();
    },

    addWTimeAreaHour() {
      const areaId = this.areaIds[0] || 1;
      const usedHours = this.wTimeAreaHourRowsLocal
        .filter((r) => r.areaId === areaId)
        .map((r) => this.parseHour(r.hour))
        .filter((h) => h != null);
      let hour = 17;
      while (usedHours.includes(hour) && hour < 24) hour += 1;
      this.wTimeAreaHourRowsLocal = [
        ...this.wTimeAreaHourRowsLocal,
        { uid: nextRowUid(), areaId, hour, weight: 1 }
      ];
      this.markDirty();
    },

    removeWTimeAreaHour(index) {
      this.wTimeAreaHourRowsLocal = this.wTimeAreaHourRowsLocal.filter((_, i) => i !== index);
      this.markDirty();
    },

    onReset() {
      this.$confirm("确认恢复 W_loc / W_time / r_pts / θ / γ / β 为前端默认种子值并写入服务端？", "恢复默认", {
        type: "warning"
      }).then(() => {
        this.resetting = true;
        const seed = createSeedTunableConfig();
        this.config = this.normalizeConfig(seed);
        this.syncWTimeRowsFromConfig();
        const payload = this.buildPayload();
        return applyEvTunableConfig(payload)
          .then((res) => {
            this.config = this.normalizeConfig(res.data);
            this.syncWTimeRowsFromConfig();
            this.usingSeed = false;
            this.dirty = false;
            this.$message.success("已恢复默认配置");
          })
          .finally(() => {
            this.resetting = false;
          });
      });
    }
  }
};
</script>

<style lang="less" scoped>
.ev-config-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.config-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.hint {
  font-size: 12px;
  color: #909399;
}

.form-hint {
  margin-left: 8px;
  font-size: 12px;
  color: #909399;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.config-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
}

.section-desc {
  margin: 0 0 8px;
  font-size: 12px;
  color: #909399;
}

.section-actions {
  margin-bottom: 8px;
}

.compact-table {
  max-width: 520px;
}

::v-deep .el-collapse-item__header {
  font-size: 13px;
  font-weight: 600;
  height: 36px;
  line-height: 36px;
}

::v-deep .el-collapse-item__content {
  padding-bottom: 12px;
}
</style>
